import React, { useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';

import { getCurrency, getLanguage, getMarket } from '@travel/i18n';
import { isNotEmptyArray } from '@travel/utils';

import ProviderSummary, { Props as ProviderSummaryProps } from 'components/ProviderSummary';
import Swiper from 'components/Swiper';
import { EMPTY_ARRAY } from 'constants/defaultValue';
import { Link } from 'core/universalRouter/Link';
import paths from 'core/universalRouter/paths';
import { getItems } from 'store/providerList/selectors';

import { ProviderItem } from 'ProviderList-Types';

import { getIsGlobalCurrency } from 'store/markets/selectors';

import styles from './providerSummarySlider.module.scss';

type Props = {
  /** String of selected provider id for initial state */
  selectedProviderId: string;
  /** Callback to be called when active slide is updated */
  onSlideChange?: (item: ProviderItem) => void;
};

const SLIDER_SPEED = 150;

/**
 * Note: This slider will render only 3 items which are the previous, main (selected) and next item
 */
function ProviderSummarySlider(props: Props) {
  const cache = useRef<{ [key: string]: ProviderSummaryProps }>({});

  const { providers = EMPTY_ARRAY } = useSelector(getItems);
  const language = useSelector(getLanguage);
  const currency = useSelector(getCurrency);
  const market = useSelector(getMarket);
  const isGlobalCurrency = useSelector(getIsGlobalCurrency);

  const mainProviderIndex = providers?.findIndex(
    provider => props.selectedProviderId === provider?.id,
  );

  // NOTE: To avoid -1 as an index
  // For example, when the first provider is selected, the slide at index 0, 1, 2 will be rendered
  const diff = mainProviderIndex === 0 ? 0 : 1;
  const providerIndexes = Array(3)
    .fill(0)
    .reduce((result, _item, index) => {
      const nextIndex = mainProviderIndex - diff + index;
      return [...result, nextIndex];
    }, []);

  const onChange = (_currentIndex: number, nextIndex: number) => {
    // NOTE: Set time + 100 is to make sure that the component will be updated after slide was changed
    setTimeout(
      () => props.onSlideChange?.(providers?.[providerIndexes[Math.max(0, nextIndex)]]),
      SLIDER_SPEED,
    );
  };

  // NOTE: to cache a computed props that will be reused when state is updated
  const getProviderProps = useCallback(
    (provider: ProviderItem) => {
      if (cache.current[provider?.id]) {
        return cache.current[provider?.id];
      }

      const propsObj = {
        id: provider.id,
        price: provider.price,
        name: provider.name,
        reviewScore: provider.reviews.score,
        totalReviews: provider.reviews.count,
        reviewLabel: provider.reviews.label,
        starRating: provider.starRating,
        language: language,
        currency: currency,
        isGlobalCurrency: isGlobalCurrency,
        market: market.marketCode,
        media: provider.media?.[0],
      };

      cache.current[provider?.id] = propsObj;
      return propsObj;
    },
    [currency, isGlobalCurrency, language, market.marketCode],
  );

  if (!isNotEmptyArray(providerIndexes) || !isNotEmptyArray(providers)) {
    return null;
  }

  return (
    <div data-testid="providerSummarySlider-wrapper" className={styles.wrapper}>
      <Swiper
        onChange={onChange}
        // NOTE: To display a slide at index 1 when the first provider is selected
        initialSlide={mainProviderIndex === 0 ? 0 : 1}
        // NOTE: To re-render when the selected provider updated
        // because we need to reset an initial slide
        key={props.selectedProviderId}
        paginationType="arrows"
        shouldRenderAllChild={false}
      >
        {providerIndexes?.map((providerIndex, index) =>
          providers?.[providerIndex] ? (
            <Link
              to={paths.providerInfo.pathResolver(providers?.[providerIndex].pathId)}
              className={styles.slideWrapper}
              key={`${providerIndex}-${index}`}
            >
              <ProviderSummary
                className={styles.slide}
                isPromotionLabelHidden
                // NOTE: To prevent image flickering
                isDisableImgLazyload
                {...getProviderProps(providers?.[providerIndex])}
              />
            </Link>
          ) : null,
        )}
      </Swiper>
    </div>
  );
}

export default ProviderSummarySlider;
